/* eslint-disable react-hooks/exhaustive-deps */
/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-16 11:51:42
 *
 *******************************************************/
//@flow

import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import { Icon, SkeletonBodyText, TextStyle, Tooltip } from '@shopify/polaris';
import { ChevronLeftMinor, ChevronRightMinor } from '@shopify/polaris-icons';
import { withI18n } from '@shopify/react-i18n';
import { connect } from 'react-redux';

import ProductsGrid from 'src/components/views/test-your-search/products/products-grid';
import ProductsCompare from 'src/components/views/test-your-search/products/products-compare';

import { waitFor } from 'src/helpers/time-helper';

import { ComposedPropsProducts } from './index.type';

const NUMBER_PRODUCTS_PER_PAGE = 18;

// Render component
const ProductsPreview = (props: ComposedPropsProducts) => {
  const {
    i18n,
    loading,
    showLoading,
    buildQueries,
    query,
    moneyFormat,
    testSearchData,
    searchFeatures,
    getProducts,
    products,
    enablePPR,
    total
  } = props;
  const { performance_ranking = [] } = testSearchData;
  const { redirects = {} } = searchFeatures;
  const hasRedirect = !_.isEmpty(redirects);
  const hasProductPerformanceRanking =
    enablePPR && performance_ranking && performance_ranking.length > 0;
  const [page, setPage] = useState(1);
  const [enableCompare, setEnableCompare] = useState(false);

  useEffect(() => {
    if (!hasProductPerformanceRanking && enableCompare) {
      setEnableCompare(false);
    }
  }, [hasProductPerformanceRanking]);

  // Reset page when query change
  useEffect(() => {
    setPage(1);
  }, [query]);
  // Get product per page
  const getProductsByPage = async page => {
    await waitFor(300);

    if (loading) {
      return;
    }
    setPage(page);
    const queries = buildQueries(page);
    await getProducts(queries);
  };

  // Render pagination bar
  const renderPaginationBar = () => {
    let previewPage = page;
    // ensure data page is number
    if (typeof page !== 'number') {
      previewPage = 1;
    }
    // Calculate total page
    const totalPage = Math.ceil(total / NUMBER_PRODUCTS_PER_PAGE);

    // Initialize rule items
    let rules = [];
    if (totalPage <= 4) {
      for (let i = 1; i <= totalPage; i++) {
        rules.push(i);
      }
    } else {
      // >= 5
      // Always have 3 first pages
      rules = [1, 2, 3];

      // Add ... in case of first and second page
      if (previewPage === 1 || previewPage === 2) {
        rules.push('...');
      }

      // Add page before current page
      const prevPage = previewPage - 1;
      if (prevPage > 0 && !_.includes(rules, prevPage)) {
        if (prevPage > 4) {
          rules.push('...');
        }
        rules.push(prevPage);
      }

      // Add current page
      if (!_.includes(rules, previewPage)) {
        rules.push(previewPage);
      }

      // Add page after current page
      const nextPage = previewPage + 1;
      if (nextPage <= totalPage && !_.includes(rules, nextPage)) {
        rules.push(nextPage);
        if (nextPage < totalPage - 1) {
          rules.push('...');
        }
      }

      // Latest page
      if (previewPage !== totalPage && !_.includes(rules, totalPage)) {
        rules.push(totalPage);
      }
    }

    return (
      <div className="pagination">
        {/* Previous page */}
        {previewPage > 1 ? (
          <span className="prev icon link" onClick={() => getProductsByPage(previewPage - 1, true)}>
            <Icon source={ChevronLeftMinor} color="subdued" />
          </span>
        ) : (
          <span className="prev icon disabled">
            <Icon source={ChevronLeftMinor} color="subdued" />
          </span>
        )}
        {rules.map((item, index) => {
          // Current page
          if (item !== '...' && item === previewPage) {
            return (
              <span key={'item-' + index} className="item selected">
                {item}
              </span>
            );
          }

          // Next page
          // If item is ..., get page of previous page + 1
          const pageIndex = item !== '...' ? item : rules[index - 1] + 1;

          // Another pages
          return (
            <span
              key={'item-' + index}
              className={item === '...' ? 'item link point' : 'item link'}
              onClick={() => getProductsByPage(pageIndex, true)}>
              {item}
            </span>
          );
        })}

        {/* Next page */}
        {previewPage < totalPage ? (
          <span className="next icon link" onClick={() => getProductsByPage(previewPage + 1, true)}>
            <Icon source={ChevronRightMinor} color="subdued" />
          </span>
        ) : (
          <span className="next icon disabled">
            <Icon source={ChevronRightMinor} color="subdued" />
          </span>
        )}
      </div>
    );
  };

  const renderHeader = () => {
    if (!products.length || hasRedirect) return null;
    return (
      <div className="header-product-preview">
        {hasProductPerformanceRanking && (
          <div>
            <div className="toggle">
              <label className="BC-Checkbox-Status">
                <input
                  type="checkbox"
                  checked={enableCompare}
                  value={enableCompare}
                  onChange={e => {
                    setEnableCompare(!enableCompare);
                  }}
                />
                <span />
              </label>
              <Tooltip
                preferredPosition="above"
                content={
                  <p style={{ maxWidth: '287px' }}>
                    {i18n.translate(
                      'AdminHTML.TestYourSearch.Table.CompareView.toggleTooltip_Content'
                    )}
                  </p>
                }>
                <span>
                  {i18n.translate('AdminHTML.TestYourSearch.Table.CompareView.toggle_Label')}
                </span>
              </Tooltip>
            </div>
          </div>
        )}

        {!enableCompare && (
          <>
            <div>
              {i18n.translate(`AdminHTML.Common.Duration.showResult`, {
                count: total,
                nt_number: i18n.formatNumber(total)
              })}
            </div>
            {renderPaginationBar()}
          </>
        )}
      </div>
    );
  };

  const renderResults = () => {
    if (hasRedirect && !loading) {
      return (
        <div className="no-result">
          {i18n.translate('AdminHTML.TestYourSearch.Table.Feature.redirects_Detail', {
            q: <TextStyle variation="strong">{query}</TextStyle>,
            link: (
              <a
                target="_blank"
                rel="noopener noreferrer"
                href={_.get(redirects, 'data.redirectTo', '#')}>
                {_.get(redirects, 'data.redirectTo', '#')}
              </a>
            )
          })}
        </div>
      );
    }

    if (!products.length) {
      return (
        <div className="no-result">
          {i18n.translate('AdminHTML.TestYourSearch.TestYourSearch.noResult')}
        </div>
      );
    }

    if (hasProductPerformanceRanking && enableCompare) {
      return <ProductsCompare query={query} performanceRanking={performance_ranking} />;
    }
    return <ProductsGrid products={products} moneyFormat={moneyFormat} />;
  };

  return (
    <div className="product-preview">
      {showLoading ? (
        <div>
          <SkeletonBodyText lines={3} />
          <SkeletonBodyText lines={3} />
          <SkeletonBodyText lines={3} />
        </div>
      ) : (
        <>
          {/* Pagination */}
          {renderHeader()}
          {renderResults()}
        </>
      )}
    </div>
  );
};

const mapDispatchToProps = (dispatch, ownProps) => ({});
const mapStateToProps = state => {
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', '');
  const products = _.get(state.product, 'searchProducts', []);
  const totalProducts = _.get(state.product, 'totalProducts', 0);
  // Get format money, help to display price
  const moneyFormat = _.get(state.oauth.tenantApp, 'externalInfo.money_format', '');
  const testSearchData = _.get(state.product, 'productData.test_search_data', {});
  return {
    tenantId,
    products,
    testSearchData,
    total: totalProducts,
    searchFeatures: _.get(state.searchPreview, 'features', {}),

    loading: _.get(state.searchPreview, 'loading', false),
    moneyFormat
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(ProductsPreview));
